import { Box, IconButton, Stack, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import type { ProductCreationDraftRecord } from "../newProduct/draftApi";
import { productCreationDraftProgress } from "../newProduct/draftApi";

interface DraftProductCardProps {
    draft: ProductCreationDraftRecord;
    onResume: (draft: ProductCreationDraftRecord) => void;
    onDelete: (draft: ProductCreationDraftRecord) => void;
}

export const DRAFT_BLUE = {
    main: "#1954ED",
    deep: "#103699",
    soft: "#EDF2FF",
    border: "#A3BBF8",
    wash: "#F7F9FF"
} as const;

export const draftDisplayName = (draft: ProductCreationDraftRecord): string =>
    draft.draft_state.state.fields.name.trim() || "Untitled product";

export const DraftBadge = () => {
    return (
        <Box
            component="span"
            sx={{
                px: 0.75,
                py: 0.125,
                borderRadius: 0.75,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: DRAFT_BLUE.deep,
                backgroundColor: DRAFT_BLUE.soft,
                border: `1px solid ${DRAFT_BLUE.border}`,
                flex: "0 0 auto"
            }}
        >
            Draft
        </Box>
    );
};

const DraftProductCard = ({ draft, onResume, onDelete }: DraftProductCardProps) => {
    const theme = useTheme();
    const progress = productCreationDraftProgress(draft);
    const name = draftDisplayName(draft);

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={() => onResume(draft)}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onResume(draft);
                }
            }}
            aria-label={`Resume draft ${name}`}
            data-ui-label={`Resume draft ${name}`}
            data-ui-role="button"
            data-ui-group="products"
            sx={{
                cursor: "pointer",
                borderRadius: 1.5,
                border: `1.5px dashed ${DRAFT_BLUE.border}`,
                background: `linear-gradient(135deg, ${DRAFT_BLUE.soft} 0%, ${DRAFT_BLUE.wash} 100%)`,
                p: 2,
                minHeight: 178,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                transition: "border-color .15s, box-shadow .15s",
                "&:hover": {
                    borderColor: DRAFT_BLUE.main,
                    boxShadow: `0 6px 18px ${alpha(DRAFT_BLUE.main, 0.22)}`
                },
                "&:focus-visible": {
                    outline: `2px solid ${DRAFT_BLUE.main}`,
                    outlineOffset: 2
                }
            }}
        >
            <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        backgroundColor: DRAFT_BLUE.soft
                    }}
                >
                    <EditNoteOutlinedIcon sx={{ fontSize: 19, color: DRAFT_BLUE.main }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography
                            sx={{
                                fontSize: 14.5,
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                letterSpacing: "-0.005em",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}
                        >
                            {name}
                        </Typography>
                        <DraftBadge />
                    </Stack>
                    <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary, lineHeight: 1.4, mt: 0.25 }}>
                        {progress.completed} of {progress.total} sections complete
                    </Typography>
                </Box>
            </Stack>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 11 }} />
                <Typography component="span" sx={{ fontSize: 11 }}>Saved</Typography>
                <Typography
                    component="span"
                    sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: 11,
                        color: theme.palette.text.primary,
                        fontWeight: 500
                    }}
                >
                    {new Date(draft.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </Typography>
            </Stack>

            <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{ pt: 1.25, borderTop: `1px solid ${DRAFT_BLUE.border}` }}
            >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: DRAFT_BLUE.main }}>
                    Continue creating
                </Typography>
                <ArrowForwardIcon sx={{ fontSize: 13, color: DRAFT_BLUE.main }} />
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Delete draft" arrow>
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            onDelete(draft);
                        }}
                        sx={{
                            width: 32,
                            height: 32,
                            color: theme.palette.text.secondary,
                            "&:hover": {
                                backgroundColor: alpha(theme.palette.error.main, 0.08),
                                color: theme.palette.error.main
                            }
                        }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
};

export default DraftProductCard;
